import { useAuth } from './AuthContext';

/**
 * Capability checks for the UI.
 *
 * The list comes from the server on sign-in, so there is no second copy of the
 * permission map in the client. Hiding a control is a courtesy (NFR-07) — the
 * server refuses the request regardless of what the UI renders.
 */
/**
 * The first page a user can actually use.
 *
 * Not everyone can read requisitions — a vendor's admin or the platform Super
 * Admin would land on an error page if /jobs were the universal default.
 */
export const landingPathFor = (user) => {
  const permissions = user?.permissions || [];
  const roles = user?.roles || [user?.role].filter(Boolean);
  const candidate = roles.includes('CANDIDATE');
  if (candidate && permissions.includes('page.dashboard')) return '/dashboard';
  if (candidate && permissions.includes('page.candidate_workspace')) return '/candidate-workspace';
  // The platform operator opens on the dashboard: the platform's numbers first, the shortcuts in the other tab. Where the dashboard
  // has been hidden from the role (page access is configurable), the first page that is left is used, as for everybody.
  if (roles.includes('SUPER_ADMIN') && permissions.includes('page.dashboard')) return '/dashboard';
  // The staff of an employer using Interview as a Service open on Screening: it is the whole of what they have.
  if (permissions.includes('page.screening') && !permissions.includes('page.jobs')) return '/screening';
  if (permissions.includes('page.tasks')) return '/tasks';
  if (permissions.includes('page.jobs')) return '/jobs';
  if (permissions.includes('page.assigned_roles')) return '/vendor-dashboard';
  if (permissions.includes('page.job_board') && !permissions.includes('page.organizations')) return '/job-board';
  if (permissions.includes('page.organizations')) return '/organizations';
  if (permissions.includes('page.administration')) return '/administration';
  if (permissions.includes('page.dashboard')) return '/dashboard';
  if (permissions.includes('page.profile')) return '/profile';
  return '/no-access';
};

const usePermissions = () => {
  const { user } = useAuth();
  const permissions = user?.permissions || [];

  const can = (capability) => permissions.includes(capability);
  const canAny = (...capabilities) => capabilities.some(can);

  // The Dataflix operator sees every organization, so some views add a
  // column that would be meaningless for anyone else.
  const isPlatformOperator = user?.role === 'SUPER_ADMIN';

  return { can, canAny, permissions, role: user?.role, isPlatformOperator };
};

export default usePermissions;
